import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

interface FormSectionProps {
  number: number;
  title: string;
  children: React.ReactNode;
  className?: string;
}

export const FormSection = ({ number, title, children, className = "" }: FormSectionProps) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: number * 0.05 }}
    className={`rounded-xl border border-border bg-card p-6 card-shadow ${className}`}
  >
    <h2 className="mb-4 flex items-center gap-3 text-lg font-bold text-foreground">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
        {number}
      </span>
      {title}
    </h2>
    <div className="space-y-4">{children}</div>
  </motion.section>
);

interface CheckboxCardProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children?: React.ReactNode;
  className?: string;
}

export const CheckboxCard = ({ id, label, checked, onChange, children, className = "" }: CheckboxCardProps) => (
  <div className={className}>
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-smooth ${
        checked ? "border-primary bg-forest-light" : "border-border hover:border-primary/40"
      }`}
    >
      <div
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-smooth ${
          checked ? "border-primary bg-primary" : "border-muted-foreground/40"
        }`}
      >
        {checked && <Check className="h-3 w-3 text-primary-foreground" />}
      </div>
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span className="text-sm font-semibold text-foreground">{label}</span>
    </label>
    {checked && children && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        className="mt-2 ml-8"
      >
        {children}
      </motion.div>
    )}
  </div>
);

interface RadioOptionProps {
  name: string;
  value: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: string) => void;
  icon?: string;
}

export const RadioOption = ({ name, value, label, description, checked, onChange, icon }: RadioOptionProps) => (
  <label
    className={`flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 transition-smooth ${
      checked ? "border-primary bg-forest-light" : "border-border hover:border-primary/40"
    }`}
  >
    <div
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-smooth ${
        checked ? "border-primary" : "border-muted-foreground/40"
      }`}
    >
      {checked && <div className="h-2.5 w-2.5 rounded-full bg-primary" />}
    </div>
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="sr-only"
    />
    <div>
      <span className="text-sm font-bold text-foreground">
        {icon && <span className="mr-1">{icon}</span>}
        {label}
      </span>
      {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
    </div>
  </label>
);

interface FormInputProps {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  hint?: string;
}

export const FormInput = ({ label, name, type = "text", placeholder, value, onChange, required, hint }: FormInputProps) => (
  <div>
    <label htmlFor={name} className="mb-1 block text-sm font-semibold text-foreground">
      {label} {required && <span className="text-coral">*</span>}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20 transition-smooth"
    />
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

interface FileUploadProps {
  label: string;
  name: string;
  hint?: string;
  currentFile?: string;
}

export const FileUpload = ({ label, name, hint, currentFile }: FileUploadProps) => (
  <div>
    <label className="mb-1 block text-sm font-semibold text-foreground">{label}</label>
    {currentFile && (
      <p className="mb-1 text-xs text-muted-foreground">
        Arquivo atual: <a href={currentFile} target="_blank" rel="noopener noreferrer" className="text-coral underline">ver</a>
      </p>
    )}
    <input
      type="file"
      name={name}
      accept=".jpg,.png,.pdf"
      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1 file:text-xs file:font-semibold file:text-primary-foreground hover:file:bg-forest-dark transition-smooth"
    />
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);
